import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchTours } from "../lib/api";
import { CATEGORY_META } from "../mock";
import TourCard from "../components/TourCard";
import SEO from "../components/SEO";
import { Loader2 } from "lucide-react";
import { useSettings } from "../lib/settings";

const TourCategoryPage = () => {
  const { category } = useParams();
  const { settings, loading: settingsLoading } = useSettings();
  const dynamicMeta = (settings?.tour_categories || []).find((c) => c.key === category);
  const fallbackMeta = CATEGORY_META[category];
  const meta = dynamicMeta || fallbackMeta;
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchTours(category)
      .then((data) => setTours(data))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, [category]);

  if (settingsLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="container-x section text-center">
        <h2 className="font-serif-display text-[32px]">Category not found</h2>
        <Link to="/" className="btn-outline mt-6 inline-block">Back home</Link>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title={`${meta.title} · Free Istanbul Tours`}
        description={meta.description}
        path={`/tours/${category}`}
        image={meta.image}
        jsonLd={tours.length > 0 ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: tours.slice(0, 20).map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${window.location.origin}/tour/${t.slug || t.id}`,
            name: t.title,
          })),
        } : undefined}
      />
      <section className="relative min-h-[440px] text-white overflow-hidden">
        <img src={meta.image} alt={meta.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative container-x min-h-[440px] flex flex-col justify-end pb-14">
          <p className="eyebrow text-[#ffb9a8] mb-3">{meta.tagline}</p>
          <h1 className="font-serif-display text-[40px] md:text-[56px] leading-[1.05] max-w-[820px]">
            {meta.title}
          </h1>
          <p className="mt-4 max-w-[600px] text-[16px] text-[#f6ecdf]">
            {meta.description}
          </p>
        </div>
      </section>

      <section className="section bg-[var(--ri-cream)]">
        <div className="container-x">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="font-serif-display text-[26px]">No tours in this category yet.</h3>
              <p className="mt-3 text-[var(--ri-muted)]">Check back soon or contact us for a private option.</p>
              <Link to="/contact" className="btn-primary mt-6 inline-block">Contact us</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tours.map((t) => (
                <TourCard key={t.id} tour={t} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TourCategoryPage;
